/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from '@mui/material/Card'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDAlert from 'components/MDAlert'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDButton from 'components/MDButton'
import { Tooltip, IconButton, Modal, CardContent, Checkbox, Badge, CardMedia, Chip } from '@mui/material'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@material-ui/core'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { useState, useEffect } from 'react'
import { AbilityContext, Can } from 'Can'
import { useAbility } from '@casl/react'
import getId from 'services/helper-service'
import moment from 'moment'
import { JSONTree } from 'react-json-tree'
import { useMediaQuery } from 'react-responsive'
import AddItem from './components/addItem'
import { toast, ToastContainer } from 'react-toastify'
import EditItem from './components/editItem'
import { CopyAll } from '@mui/icons-material'

function StorageManagement() {

    const [data, setData] = useState([])
    const [tableData, setTableData] = useState([])
    const [openViewModal, setOpenViewModal] = useState(false)
    const [openEditModal, setOpenEditModal] = useState(false)
    const [openImgModal, setOpenImgModal] = useState(false)
    const [loader, setLoader] = useState(false)
    const [selectedItem, setSelectedItem] = useState();
    const [selectedUrl, setSelectedUrl] = useState('');
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' })
    const ability = useAbility(AbilityContext)

    useEffect(() => {
        ; (async () => {
            callApi()
        })()
        // document.title = `Rivio | Webhook Logs`
    }, [])

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    const callApi = async () => {
        setLoader(true)
        const response = await CrudService.getStorageList()
        setData(response.data)
        setLoader(false)
    }

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!confirm('Are you sure you want to delete this item?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                const result = await CrudService.deleteStorageList(id)
                toast.info('Successfully removed')

                // the delete does not send a response
                // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
                callApi()
            }
        } catch (err) {
            // it sends error is the device is associated with an device
            console.error(err)
            if (err.hasOwnProperty('errors')) {
                toast(err.errors[0].detail)
            }
            return null
        }
    }

    const clickViewHandler = () => {
        setOpenViewModal(true)
    }

    const handleViewClose = () => {
        setOpenViewModal(false)
    }

    const clickEditHandler = (value) => {
        setSelectedItem(value)
        setOpenEditModal(true)
    }

    const handleEditClose = () => {
        setOpenEditModal(false)
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text)
            .then(() => {
                toast.info("Copied!")
            })
            .catch((error) => {
                console.error("Failed to copy text:", error);
            });
    }

    function openImgModel(value) {
        setSelectedUrl(value)
        setOpenImgModal(true)
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {
            return {
                id: row.id,
                value: row.value,
                url: row.url,
                tags: row.tags?.length ? row.tags : [],
                ...row
            }
        })
        return updatedInfo
    }

    const dataTableData = {
        columns: [
            { Header: 'ID', accessor: 'id', width: '10%' },
            { Header: 'Value', accessor: 'value', width: '10%' },
            {
                Header: 'Tags', accessor: 'tags', width: '60%',
                Cell: (info) => {
                    return (
                            info.cell.value?.map((e) => (
                                <Chip
                                    key={e}
                                    tabIndex={-1}
                                    label={e}
                                    sx={{ mx: 0.3 }}
                                />
                            ))
                    )
                },
            },
            {
                Header: 'Image',
                accessor: 'url',
                isSortedDesc: true,
                isSorted: true,
                width: '10%',
                Cell: (info) => {
                    return (
                        <img src={info.cell.value} width={50} onClick={() => openImgModel(info.cell.value)} />
                    )
                },
            },
            {
                Header: 'Target',
                accessor: '',
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('view', 'storage') && (
                                <Tooltip title="Copy Url">
                                    <IconButton
                                        onClick={() => copyToClipboard(info.cell.row.original.url)}
                                    >
                                        <MDTypography>
                                            <CopyAll />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                            {ability.can('edit', 'storage') && (
                                <Tooltip title="Edit Item">
                                    <IconButton
                                        onClick={() => clickEditHandler(info.cell.row.original)}
                                    >
                                        <MDTypography>
                                            <EditIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                            {ability.can('delete', 'storage') && (
                                <Tooltip title="Delete Item">
                                    <IconButton
                                        onClick={(e) =>
                                            clickDeleteHandler(e, info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <DeleteIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                },
            },
        ],

        rows: tableData,
    }

    const renderTableCell = (data) => {
        // If the data is an object (JSON), render each key-value pair
        if (typeof data === 'object' && data !== null) {
            return (
                <Table>
                    <TableBody>
                        {Object.entries(data).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell>{key}</TableCell>
                                <TableCell>{renderTableCell(value)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )
        }
        return data
    }

    const onNewItemSubmit = async () => {
        handleViewClose();
        setTimeout(() => {
            callApi();
        }, 1000)
    }

    const onEditItemSubmit = async () => {
        handleEditClose();
        setTimeout(() => {
            callApi();
        }, 1000)
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <ToastContainer />
            <MDBox pt={1} >
                <MDBox>
                    <Card>
                        <MDBox
                            px={3}
                            py={1}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                {/* Device Management */}
                            </MDTypography>

                            {ability.can('create', 'storage') && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickViewHandler}
                                >
                                    + New Item
                                </MDButton>
                            )}
                        </MDBox>
                        <DataTable
                            table={dataTableData}
                            canSearch={true}
                            // canRefresh={true}
                            isSorted={true}
                            entriesPerPage={{ defaultValue: 25 }}
                            searchFun={callApi}
                            loader={loader}
                            deepSearch={true}
                            tableHeight="calc(100vh - 140px)"
                            fixedWidth={true}
                        />
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />
            <Modal
                open={openViewModal}
                onClose={handleViewClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        // width: '80%', // Set the desired width here
                        backgroundColor: '#f0f2f5',
                        boxShadow: 24,
                        // p: isTabletOrMobile ? 0 : 4,
                        maxHeight: '70vh', // Set max height to enable scrolling
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        // height: '70vh'
                    }}
                >
                    <CardContent>
                        {/* <DynamicDataTable EventData={paramTableData} /> */}
                        <div style={{ display: isTabletOrMobile ? 'block' : 'flex', justifyContent: 'space-between' }}>
                            <AddItem onNewItemSubmit={onNewItemSubmit} />
                        </div>
                    </CardContent>
                </Card>
            </Modal>
            <Modal
                open={openEditModal}
                onClose={handleEditClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        // width: '80%', // Set the desired width here
                        backgroundColor: '#f0f2f5',
                        boxShadow: 24,
                        // p: isTabletOrMobile ? 0 : 4,
                        maxHeight: '70vh', // Set max height to enable scrolling
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        // height: '70vh'
                    }}
                >
                    <CardContent>
                        {/* <DynamicDataTable EventData={paramTableData} /> */}
                        <div style={{ display: isTabletOrMobile ? 'block' : 'flex', justifyContent: 'space-between' }}>
                            <EditItem onEditItemSubmit={onEditItemSubmit} selectedItem={selectedItem} />
                        </div>
                    </CardContent>
                </Card>
            </Modal>
            <Modal
                open={openImgModal}
                onClose={() => setOpenImgModal(false)}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        // width: '80%', // Set the desired width here
                        backgroundColor: '#f0f2f5',
                        boxShadow: 24,
                        maxHeight: '50vh', // Set max height to enable scrolling
                        maxWidth: '50vw', // Set max height to enable scrolling
                        width: '250px',
                        height: '300px',
                        overflow: 'hidden'
                    }}
                >
                    <CardMedia
                        component="img"
                        image={selectedUrl}
                        alt={selectedUrl}
                        sx={{ height: '100%', width: '100%', margin: 0, padding: 0, objectFit: 'contain' }}
                    />
                </Card>
            </Modal>
        </DashboardLayout>
    )
}

export default StorageManagement
